import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { IconSaxIconName } from '@/components/design-system/atoms/icon/iconsax-icon/types';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';

interface StatCardProps {
  icon: React.ReactElement | IconSaxIconName;
  value: string | number | null;
  label: string;
  path?: string;
  displayDays?: boolean;
  direction?: 'up' | 'down' | null;
  prevValue?: string | number;
  prevPeriod?: string;
  description?: string;
  verdict?: 'good' | 'bad' | null;
}

const tooltipStyles = {
  bgcolor: '#E9ECEF',
  borderRadius: '16px',
  width: '343px',
  height: '181px',
  boxShadow: '0px 8px 24px 0px #71829126',
  '& .MuiTooltip-arrow': {
    color: '#E9ECEF',
    '&::before': {
      backgroundColor: '#E9ECEF',
      border: 'none',
    },
  },
  '& .MuiTooltip-tooltip': {
    marginTop: '8px !important',
  },
  p: 0,
};

const TooltipContent = ({
  description,
  prevValue,
  prevPeriod,
  label,
}: Pick<StatCardProps, 'description' | 'prevValue' | 'prevPeriod' | 'label'>) => (
  <div className="h-[100px] min-w-[180px] p-4">
    <div className="mb-4">
      <div className="font-sans text-[16px] font-bold text-[#212529]">{label}</div>
      <div className="font-sans text-[14px] text-[#212529]">{description}</div>
    </div>
    <div className="mt-1 flex items-center align-bottom font-sans text-[14px] text-[#212529]">
    {prevPeriod}:&nbsp;
      <span className="font-bold">{prevValue}</span>
    </div>
  </div>
);

const StatCardContent = ({
  icon,
  value,
  label,
  displayDays,
  direction,
  prevValue,
  verdict,
}: StatCardProps) => (
  <>
    <Box className="flex h-10 flex-shrink-0 items-center justify-center">
      <IconSaxIcon name={icon} size={40} strokeColor="none" />
    </Box>
    <Box className="flex flex-col gap-y-1">
      <Box className="flex items-center gap-1">
        <Typography
          variant="lead1"
          className="text-lead1 font-extrabold leading-[27px] text-[#212529]"
        >
          {value === 'NaN%' ? '0' : value ?? 'Loading...'}
        </Typography>
        {displayDays && (value as number) >= 0 && (
          <Typography
            variant="lead1"
            className="font-sans text-[14px] font-bold leading-[21px] text-[#212529]"
          >
            Day/s
          </Typography>
        )}
        {direction && (
          <Box
            component="span"
            className="align-top"
            title={`Previous: ${prevValue}`}
            sx={{
              color: verdict === 'good' ? '#158e80' : '#eb2a34',
              marginLeft: '4px',
            }}
          >
            <IconSaxIcon
              name={direction === 'up' ? 'stats-trend-up' : 'stats-trend-down'}
              size={24}
              strokeColor={verdict === 'good' ? '#158e80' : '#eb2a34'}
              className="mt-2"
            />
          </Box>
        )}
      </Box>
      <Typography variant="lead1" className="text-[14px] font-medium leading-[21px] text-[#6C757D]">
        {label}
      </Typography>
    </Box>
  </>
);

export const StatCard = (props: StatCardProps) => {
  const { description, path, label, prevValue, prevPeriod, displayDays } = props;

  const cardContent = (
    <Box
      className="flex h-10 flex-shrink-0 items-center gap-2 rounded-lg bg-white p-4 shadow-small"
      onClick={path ? () => window.open(path, '_new') : undefined}
      style={{ cursor: description || path ? 'pointer' : 'default' }}
    >
      <StatCardContent {...props} />
    </Box>
  );

  if (!description) {
    return cardContent;
  }

  const prevValueLabel = displayDays ? `${prevValue} ${Number(prevValue) > 1 ? 'days' : 'day'}` : prevValue;

  return (
    <Tooltip
      title={<TooltipContent description={description} label={label} prevValue={prevValueLabel} prevPeriod={prevPeriod} />}
      arrow
      placement="bottom"
      slotProps={{
        tooltip: {
          sx: tooltipStyles,
        },
      }}
    >
      {cardContent}
    </Tooltip>
  );
};
